import React from 'react';

import { Button } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { useHistory, useParams } from 'react-router-dom';
import ToggleSwitch from 'react-switch';

import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  PRIMARY_COLOR,
} from 'components/upb_viewer_beta/css/betaTokens';
import DpMetricsView from 'components/upb_match_explorer/DpMetricsView';
import DpSearchSelect from 'components/upb_match_explorer/DpSearchSelect';
import MatchBandView from 'components/upb_match_explorer/MatchBandView';
import MatchDistributionStrip from 'components/upb_match_explorer/MatchDistributionStrip';
import MatchRangeTiles from 'components/upb_match_explorer/MatchRangeTiles';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useUpbMatchBand from 'components/upb_match_explorer/hooks/useUpbMatchBand';
import useUpbMatchSummary from 'components/upb_match_explorer/hooks/useUpbMatchSummary';

const BASE_URL = '/upb-match-explorer';

/** Route params arrive as strings (or undefined); coerce to a finite id or null. */
function toId(value) {
  const num = Number(value);
  return value !== undefined && Number.isFinite(num) ? num : null;
}

/**
 * UPBMatchExplorerContainer — overview → band → DP drill-down.
 *
 * bandIndex/dpId live in the URL (not local state), so:
 *   - browser Back steps up one level instead of leaving the dashboard
 *   - refreshing preserves whichever view/band/partner you were on
 *   - a specific band or partner can be shared as a link
 *
 * Routes:
 *   /upb-match-explorer                          → overview (all bands)
 *   /upb-match-explorer/band/:bandIndex          → partners within a band
 *   /upb-match-explorer/band/:bandIndex/dp/:dpId → a partner drilled into from that band
 *   /upb-match-explorer/dp/:dpId                 → a partner opened directly (search or a
 *                                                   shared link)
 */
function UPBMatchExplorerContainer() {
  const history = useHistory();
  const { bandIndex: bandIndexParam, dpId: dpIdParam } = useParams();

  const bandIndex = toId(bandIndexParam);
  const dpId = toId(dpIdParam);
  // eslint-disable-next-line no-nested-ternary
  const view = dpId !== null ? 'dp' : bandIndex !== null ? 'band' : 'overview';

  const [influencedOnly, setInfluencedOnly] = useLocalStorage(
    'upbMatchExplorer.influencedOnly',
    false,
  );

  const { data: summary, loading: summaryLoading } = useUpbMatchSummary({ influencedOnly });
  const { data: bandData, loading: bandLoading } = useUpbMatchBand({
    bandIndex,
    influencedOnly,
    skip: view !== 'band',
  });

  const openBand = index => history.push(`${BASE_URL}/band/${index}`);
  const openDp = entityId => {
    // Nest under the current band (if any) so Back returns to that band rather
    // than the overview; a DP opened via search with no band context hangs
    // directly off the base route.
    const target =
      bandIndex !== null
        ? `${BASE_URL}/band/${bandIndex}/dp/${entityId}`
        : `${BASE_URL}/dp/${entityId}`;
    history.push(target);
  };
  const goOverview = () => history.push(BASE_URL);
  const goBackFromDp = () =>
    history.push(bandIndex !== null ? `${BASE_URL}/band/${bandIndex}` : BASE_URL);

  return (
    <div className={css(styles.page)}>
      <div className={css(styles.topbar)}>
        <div className={css(styles.brand)}>
          <span className={css(styles.brandTitle)}>UPB Match Explorer</span>
          <span className={css(styles.brandSub)}>Distributor Partners</span>
        </div>
        <div className={css(styles.topbarRight)}>
          <label className={css(styles.influencedToggle)}>
            <ToggleSwitch
              onColor={PRIMARY_COLOR}
              checked={influencedOnly}
              checkedIcon={false}
              uncheckedIcon={false}
              height={20}
              width={36}
              onChange={setInfluencedOnly}
            />
            <span>Show influenced enabled</span>
          </label>
          <div className={css(styles.search)}>
            <DpSearchSelect onSelect={openDp} influencedOnly={influencedOnly} />
          </div>
        </div>
      </div>

      <div className={css(styles.content)}>
        {view === 'overview' && (
          <div>
            <div className={css(styles.pageHead)}>
              <div className={css(styles.eyebrow)}>Overview</div>
              <h1 className={css(styles.h1)}>UPB match coverage by distributor partner</h1>
              <p className={css(styles.sub)}>
                Partners bucketed by UPB match rate — the share of active products linked to a UPB
                SKU. Pick a band to drill in, or search for a partner.
              </p>
            </div>
            <div className={css(styles.tilesWrap)}>
              <MatchRangeTiles summary={summary} loading={summaryLoading} onBandClick={openBand} />
            </div>
            {summary && <MatchDistributionStrip summary={summary} />}
          </div>
        )}

        {view === 'band' && (
          <div>
            <div className={css(styles.subnav)}>
              <Button variant="outline-secondary" size="sm" onClick={goOverview}>
                ← All bands
              </Button>
            </div>
            <div className={css(styles.pageHead)}>
              <h1 className={css(styles.h1)}>{bandData?.label} UPB match</h1>
              <p className={css(styles.sub)}>
                Showing the top {bandData?.data?.length || 0} of {bandData?.total || 0} partners in
                this band, highest match first.
              </p>
            </div>
            <MatchBandView band={bandData} loading={bandLoading} onPartnerClick={openDp} />
          </div>
        )}

        {view === 'dp' && (
          <div>
            <div className={css(styles.subnav)}>
              <Button variant="outline-secondary" size="sm" onClick={goBackFromDp}>
                ← Back
              </Button>
            </div>
            <DpMetricsView entityId={dpId} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  page: { minHeight: '100vh', background: BG_2 },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    padding: '0 40px',
    height: 60,
    background: '#ffffff',
    borderBottom: `1px solid ${BORDER_2}`,
  },
  brand: { display: 'flex', flexDirection: 'column', lineHeight: 1.15 },
  brandTitle: { fontSize: 15, fontWeight: 600, color: FG_1 },
  brandSub: { fontSize: 11, color: PRIMARY_COLOR, letterSpacing: '0.02em' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 20 },
  search: { width: 340, maxWidth: '40vw' },
  influencedToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: FG_2,
    whiteSpace: 'nowrap',
  },
  content: {
    width: 'calc(100vw - 80px)',
    maxWidth: 1680,
    margin: '0 auto',
    padding: '28px 40px 64px',
  },
  pageHead: { marginBottom: 20 },
  eyebrow: {
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: PRIMARY_COLOR,
    marginBottom: 6,
  },
  h1: { fontSize: 22, fontWeight: 700, margin: 0, color: FG_1 },
  sub: { color: FG_2, fontSize: 14, marginTop: 6 },
  tilesWrap: { marginBottom: 22 },
  subnav: { marginBottom: 18 },
});

export default UPBMatchExplorerContainer;
